import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsDetailPage = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, [id]);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news/${id}`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">News article not found</h2>
          <Link to="/news" className="text-orange-500 hover:text-orange-600 font-semibold">
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/news"
            data-testid="back-to-news-btn"
            className="inline-flex items-center text-orange-500 hover:text-orange-600 font-semibold mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to News
          </Link>

          {/* Article Header */}
          <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="px-4 py-2 bg-orange-100 text-orange-600 font-semibold rounded-full">
                {news.category}
              </span>
              <div className="flex items-center text-gray-600">
                <Calendar size={16} className="mr-2" />
                <span>{new Date(news.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {news.title}
            </h1>
            
            {news.tags && news.tags.length > 0 && (
              <div className="flex items-center flex-wrap gap-2">
                <Tag size={16} className="text-gray-500" />
                {news.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Featured Image */}
          {news.image_url && (
            <div className="mb-8 rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-orange-300 to-orange-500">
              <img
                src={news.image_url}
                alt={news.title}
                className="w-full h-96 object-cover"
                onError={(e) => { 
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Article Content */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 font-medium mb-6 leading-relaxed">
                {news.excerpt}
              </p>
              <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                {news.content}
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default NewsDetailPage;